<script setup>
import { WdsAvatar, WdsButtonGhost, WdsButtonPrimary, WdsIcon, WdsPopup } from '@wds/components';
import { commonValues } from '@/config/common';

const props = defineProps({
  viewModel: {
    type: Object,
    required: true,
  },
  srpHref: {
    type: String,
    required: true,
  },
});

const {
  t,
  locale,
  defaultLocaleObj,
} = useI18n();
const { useFormatter } = useFormatters();
const { isSmallOrMediumScreen } = useMq();

const isModalOpen = ref(false);
const popupHeadlineId = useId();

const DECORATIVE_AVATARS = [
  {
    name: 'Alex',
    color: 'teal',
  },
  {
    name: 'Sam',
    color: 'purple',
  },
  {
    name: 'Jamie',
    color: 'pink',
  },
];

function formatCount(num) {
  if (!Number.isInteger(num)) {
    return '-';
  }
  const localeObject = useFormatter.getLocaleObjectByCode(locale.value) || defaultLocaleObj;
  return Intl.NumberFormat(localeObject.language).format(num);
}

const regionNames = computed(() => {
  const localeObject = useFormatter.getLocaleObjectByCode(locale.value) || defaultLocaleObj;
  try {
    return new Intl.DisplayNames([localeObject.language], { type: 'region' });
  } catch {
    return null;
  }
});

function flagUrl(countryCode) {
  return `https://a.hwstatic.com/hw/flags/${countryCode}.svg`;
}

function nationalityName(countryCode) {
  return regionNames.value?.of(countryCode) || countryCode;
}

const sheetBenefits = computed(() => [
  {
    icon: 'chat',
    title: t('t_SOCIALHUBSHEETCHATTITLE'),
    body: t('t_SOCIALHUBSHEETCHATBODY', { CITYNAME: props.viewModel.cityName }),
  },
  {
    icon: 'calendar-star',
    title: t('t_SOCIALHUBSHEETEVENTSTITLE'),
    body: t('t_SOCIALHUBSHEETEVENTSBODY'),
  },
  {
    icon: 'users',
    title: t('t_SOCIALHUBSHEETWHOSTITLE'),
    body: t('t_SOCIALHUBSHEETWHOSBODY'),
  },
]);

function handleJoinClick() {
  isModalOpen.value = true;
}

function handlePopupClose() {
  isModalOpen.value = false;
}
</script>

<template>
  <aside class="social-hub">
    <div class="hub-header">
      <h2 class="title title-5-bld">
        {{ t('t_SOCIALHUBTITLE', { CITYNAME: viewModel.cityName }) }}
      </h2>
      <div class="travellers-pill">
        <div
          class="avatar-stack"
          aria-hidden="true"
        >
          <WdsAvatar
            v-for="avatar in DECORATIVE_AVATARS"
            :key="avatar.name"
            class="avatar-stack-item"
            size="small"
            :show-text="false"
            :user-name="avatar.name"
            :color="avatar.color"
          />
        </div>
        <p class="travellers-now body-3-reg">
          <strong class="travellers-count">{{ formatCount(viewModel.travellersNow) }}+</strong>
          {{ t('t_SOCIALHUBTRAVELLERSNOW', { CITYNAME: viewModel.cityName }) }}
        </p>
      </div>
    </div>

    <div class="hub-body">
      <ul class="stat-list">
        <li class="stat-row">
          <span
            class="stat-icon"
            aria-hidden="true"
          >
            <WdsIcon
              asset="calendar-confirm"
              color="purple"
              :width="16"
              :height="16"
            />
          </span>
          <p class="stat-copy body-2-reg">
            <strong>{{ formatCount(viewModel.visitingNextMonth) }}</strong>
            {{ t('t_SOCIALHUBVISITINGNEXTMONTH', { CITYNAME: viewModel.cityName }) }}
          </p>
        </li>
        <li class="stat-row">
          <span
            class="stat-icon"
            aria-hidden="true"
          >
            <WdsIcon
              asset="chat"
              color="purple"
              :width="16"
              :height="16"
            />
          </span>
          <p class="stat-copy body-2-reg">
            <strong>{{ formatCount(viewModel.chatUsers) }}</strong>
            {{ t('t_SOCIALHUBCHATTINGNOW') }}
          </p>
        </li>
        <li class="stat-row">
          <span
            class="stat-icon"
            aria-hidden="true"
          >
            <WdsIcon
              asset="group-chat"
              color="purple"
              :width="16"
              :height="16"
            />
          </span>
          <p class="stat-copy body-2-reg">
            <strong>{{ formatCount(viewModel.messages24h) }}</strong>
            {{ t('t_SOCIALHUBMESSAGES24H') }}
          </p>
        </li>
        <li class="stat-row">
          <span
            class="stat-icon"
            aria-hidden="true"
          >
            <WdsIcon
              asset="user-line"
              color="purple"
              :width="16"
              :height="16"
            />
          </span>
          <p class="stat-copy body-2-reg">
            <strong>{{ viewModel.soloPercent }}%</strong>
            {{ t('t_SOCIALHUBSOLOPERCENT') }}
          </p>
        </li>
        <li class="stat-row">
          <span
            class="stat-icon"
            aria-hidden="true"
          >
            <WdsIcon
              asset="trophy"
              color="purple"
              :width="16"
              :height="16"
            />
          </span>
          <p class="stat-copy body-2-reg">
            <strong>{{ t('t_SOCIALHUBTOPCITIESLEAD') }}</strong>
            {{ t('t_SOCIALHUBTOPCITIES') }}
          </p>
        </li>
        <li class="stat-row stat-row-busiest">
          <span
            class="stat-icon"
            aria-hidden="true"
          >
            <WdsIcon
              asset="hostel"
              color="purple"
              :width="16"
              :height="16"
            />
          </span>
          <div class="busiest">
            <p class="busiest-label body-3-bld">
              {{ t('t_SOCIALHUBBUSIESTLABEL') }}
            </p>
            <p class="busiest-name body-1-bld">
              {{ viewModel.busiestName }}
            </p>
            <p class="busiest-staying body-3-reg">
              {{ t('t_SOCIALHUBBUSIESTSTAYING', { COUNT: formatCount(viewModel.busiestStaying) }) }}
            </p>
          </div>
        </li>
      </ul>

      <hr class="divider" />

      <div class="nationalities">
        <h3 class="nationalities-heading body-1-bld">
          {{ t('t_SOCIALHUBNATIONALITIES') }}
        </h3>
        <ul class="nationalities-list">
          <li
            v-for="item in viewModel.nationalities"
            :key="item.countryCode"
            class="nationality-row"
          >
            <span
              class="flag"
              :style="{ backgroundImage: `url('${flagUrl(item.countryCode)}')` }"
              aria-hidden="true"
            />
            <span
              class="nationality-name body-2-reg"
              :title="nationalityName(item.countryCode)"
            >{{ nationalityName(item.countryCode) }}</span>
            <span class="bar-track">
              <span
                class="bar-fill"
                :style="{ width: `${item.percent}%` }"
              />
            </span>
            <span class="percent body-3-reg">{{ item.percent }}%</span>
          </li>
        </ul>
      </div>

      <div class="join-wrap">
        <WdsButtonPrimary
          class="join-button"
          type="button"
          :text="t('t_SOCIALHUBJOINCHAT', { CITYNAME: viewModel.cityName })"
          aria-haspopup="dialog"
          :aria-expanded="isModalOpen"
          @click="handleJoinClick"
        />
      </div>
    </div>

    <WdsPopup
      class="social-hub-popup"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="popupHeadlineId"
      :is-open="isModalOpen"
      :has-header="false"
      :apply-sheet="isSmallOrMediumScreen"
      :sheet-height="0"
      close-button
      :close-button-aria-label="t('t_A11YSHEETCLOSE')"
      @popup-close="handlePopupClose"
    >
      <template #popupContent>
        <div class="popup-body">
          <div class="popup-header">
            <div class="popup-header-copy">
              <p class="popup-eyebrow body-3-bld">
                {{ t('t_SOCIALHUBSHEETEYEBROW') }}
              </p>
              <h2
                :id="popupHeadlineId"
                class="popup-headline title-2-bld"
              >
                {{ t('t_SOCIALHUBSHEETHEADLINE') }}
              </h2>
            </div>
          </div>
          <div class="popup-main">
            <ul class="benefit-list">
              <li
                v-for="benefit in sheetBenefits"
                :key="benefit.title"
                class="benefit-row"
              >
                <span
                  class="stat-icon"
                  aria-hidden="true"
                >
                  <WdsIcon
                    :asset="benefit.icon"
                    color="purple"
                    :width="16"
                    :height="16"
                  />
                </span>
                <div class="benefit-copy">
                  <p class="body-1-bld">
                    {{ benefit.title }}
                  </p>
                  <p class="body-2-reg">
                    {{ benefit.body }}
                  </p>
                </div>
              </li>
            </ul>
            <div class="popup-primary">
              <WdsButtonPrimary
                :text="t('t_SOCIALHUBSHEETBOOK')"
                :href="srpHref"
                :full-width="true"
              />
              <p class="popup-note body-3-reg">
                {{ t('t_SOCIALHUBSHEETBOOKNOTE') }}
              </p>
            </div>
            <hr class="divider" />
            <div class="popup-pass">
              <p class="body-1-bld">
                {{ t('t_SOCIALHUBSHEETPASSTITLE', { CITYNAME: viewModel.cityName }) }}
              </p>
              <p class="body-2-reg">
                {{ t('t_SOCIALHUBSHEETPASSBODY') }}
              </p>
              <WdsButtonGhost
                class="popup-pass-button"
                :text="t('t_SOCIALHUBSHEETPASSCTA')"
                :aria-label="`${t('t_SOCIALHUBSHEETPASSCTA')} (opens in a new tab)`"
                :href="commonValues.HOSTELWORLD_SOCIAL_PASS_URL"
                :full-width="true"
                rel="noopener noreferrer"
                target="_blank"
              />
            </div>
          </div>
        </div>
      </template>
    </WdsPopup>
  </aside>
</template>

<style lang="scss" scoped>
.social-hub {
  overflow: hidden;
  border: 2px solid $wds-color-purple-lightest;
  border-radius: $wds-border-radius-xl;
  color: $wds-color-ink-darker;
  background-color: $wds-color-white;
}

.hub-header {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-s;
  padding: $wds-spacing-l;
  background-color: $wds-color-purple-lightest;
}

.title {
  margin: 0;
}

.travellers-pill {
  display: flex;
  align-items: center;
  min-height: wds-rem(32px);
  background-color: $wds-color-white;
  border-radius: $wds-border-radius-m;
}

.avatar-stack {
  display: flex;
  flex-shrink: 0;
  align-items: center;
}

.avatar-stack-item {
  z-index: 1;
  box-shadow: 0 0 0 2px $wds-color-white;
  border-radius: 50%;
  margin-inline-start: wds-rem(-16px);

  &:first-child {
    margin-inline-start: 0;
  }
}

.travellers-now {
  margin: 0;
  padding-block: $wds-spacing-xs;
  padding-inline: $wds-spacing-m $wds-spacing-m;
  color: $wds-color-purple;
}

.travellers-count {
  font-weight: 700;
}

.hub-body {
  display: flex;
  flex-direction: column;
  padding: $wds-spacing-m;
}

.stat-list,
.benefit-list,
.nationalities-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.stat-list {
  display: flex;
  flex-direction: column;
}

.stat-row {
  display: flex;
  gap: $wds-spacing-s;
  align-items: center;
  padding: $wds-spacing-s;
}

.stat-row-busiest {
  align-items: flex-start;
}

.stat-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: wds-rem(32px);
  height: wds-rem(32px);
  background-color: $wds-color-purple-lightest;
  border-radius: $wds-border-radius-m;
}

.stat-copy,
.busiest,
.busiest-label,
.busiest-name,
.busiest-staying {
  margin: 0;
}

.stat-copy strong {
  margin-inline-end: $wds-spacing-xxs;
}

.divider {
  margin: 0;
  border: 0;
  border-top: 1px solid $wds-color-ink-lighter;
}

.nationalities {
  padding: $wds-spacing-m;
}

.nationalities-heading {
  margin: 0;
}

.nationalities-list {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-s;
  margin-top: $wds-spacing-s;
}

.nationality-row {
  display: grid;
  grid-template-columns: wds-rem(24px) wds-rem(88px) 1fr auto;
  gap: $wds-spacing-s;
  align-items: center;
}

.flag {
  width: wds-rem(24px);
  height: wds-rem(24px);
  border: 1px solid $wds-color-ink-lighter;
  border-radius: 50%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
}

.nationality-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bar-track {
  height: $wds-spacing-s;
  overflow: hidden;
  background-color: $wds-color-ink-lightest;
  border-radius: $wds-border-radius-xl;
}

.bar-fill {
  display: block;
  height: 100%;
  background-color: $wds-color-purple;
  border-radius: $wds-border-radius-xl;
}

.percent {
  min-width: wds-rem(32px);
  text-align: end;
}

.join-wrap {
  padding: $wds-spacing-s;
}

.join-button {
  width: 100%;
}

.social-hub-popup {
  :deep(.modal-body) {
    box-sizing: border-box;
    width: wds-rem(480px);
    max-width: 100%;
    padding: 0 !important;
  }

  :deep(.sheet-body) {
    padding: 0 !important;
  }

  :deep(.modal-container),
  :deep(.sheet-container) {
    overflow: hidden;
    border-radius: $wds-border-radius-xl;
  }
}

.popup-body {
  display: flex;
  flex-direction: column;
}

.popup-header {
  display: flex;
  align-items: flex-start;
  padding: $wds-spacing-m $wds-spacing-xl $wds-spacing-m $wds-spacing-l;
  background-color: $wds-color-purple-lightest;
}

.popup-header-copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: $wds-spacing-s;
  min-width: 0;
  padding: $wds-spacing-s;
}

.popup-eyebrow {
  margin: 0;
  color: $wds-color-purple;
  text-transform: uppercase;
}

.popup-headline {
  margin: 0;
}

.popup-main {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-s;
  padding: $wds-spacing-m $wds-spacing-l $wds-spacing-l;
}

.benefit-list {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-s;
}

.benefit-row {
  display: flex;
  gap: $wds-spacing-s;
  align-items: flex-start;
  padding-block: $wds-spacing-s;
}

.benefit-copy p,
.popup-note,
.popup-pass p {
  margin: 0;
}

.popup-primary,
.popup-pass {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-s;
  padding-block: $wds-spacing-s;
}

.popup-note {
  color: $wds-color-ink-darker;
  text-align: center;
}

.popup-pass-button {
  width: 100%;
}
</style>
